/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import { Button } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useTranslation } from "@/i18n/client";
import to from "await-to-js";
import { giftQuery } from "@/common-components-src/api/gift/gift.api";
import { NewGiftCouponDto } from "@/common-components-src/api/gift/gift.interface";
import GiftCloseIcon from "./icon/close.svg";
import JDILogin from "@/app/login/index.login";
import callAfterLogin from "@/app/login/utils/callAfterLogin";
import { EXPOSURE_RECORD, sendClickTrack } from "@/tracks/25579";
import LottieAnimation from "@/components/lottieAnimation/lottieAnimation";
import giftLottie from "./icon/giftLottie.json";
import styles from "./index.module.scss";

interface DataItemProps {
  loadCurrentOrder?: (params?: any) => void;
  isRegisterLogin?: boolean; //如果是注册登录入口，返回的优惠券信息打开弹窗
  isOpenModal?: boolean; //个人中心地址入口
  closeModalCallback?: () => void; //弹窗关闭的回调方法
}

const GiftModal: React.FC<DataItemProps> = ({ closeModalCallback }) => {
  const { t } = useTranslation();
  const [couponList, setCouponList] = useState<NewGiftCouponDto[] | null>(null); // 普通券
  const [shippingCoupons, setShippingCoupons] = useState<NewGiftCouponDto[] | null>(null); // 运费券
  const [isLogin, setIsLogin] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [activityTitle, setActivityTitle] = useState(""); //弹窗 title
  const [currencySymbol, setCurrencySymbol] = useState(""); //货币符号
  const [totalDiscount, setTotalDiscount] = useState(0); //优惠总金额

  useEffect(() => {
    loginStatus();
  }, []);

  const loginStatus = async () => {
    const { isLoggedIn, userInfo } = await JDILogin.getInstance().getUserInfo();
    setIsLogin(isLoggedIn);
    getGift(userInfo);
  };

  const getGift = async (userInfo: any) => {
    const [err, result] = await to(giftQuery({ scene: "QUERY_RECEIVE" }));
    if (err) {
      setCouponList(null);
      setShippingCoupons(null);
    } else {
      if (result?.data?.activityTitle) {
        setActivityTitle(result?.data?.activityTitle);
      }
      setCurrencySymbol(result?.data?.currencySymbol);
      setTotalDiscount(result?.data?.couponAmount);
      const list = result?.data?.couponList;

      if (list && list.length > 0 && !isLocalCouponModel(userInfo)) {
        setIsOpenModal(true);
        setCouponList(result?.data?.productCoupons);
        setShippingCoupons(result?.data?.shippingCoupons);
        saveDate();
      }
    }
  };

  const isLocalCouponModel = (userInfo: any) => {
    if (userInfo?.pin) {
      return false;
    }
    // 获取当前日期
    const currentDate = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
    const lastShown = localStorage.getItem("giftModalShown");
    // 如果日期不同，清除缓存
    if (lastShown) {
      const date = JSON.parse(lastShown)?.date;
      if (date !== currentDate) {
        localStorage.removeItem("giftModalShown");
        return false;
      }
      return true;
    }
    return false;
  };

  const saveDate = () => {
    // 记录到 localStorage，包括当前日期
    const currentDate = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
    localStorage.setItem("giftModalShown", JSON.stringify({ shown: true, date: currentDate }));
  };

  const handleClose = () => {
    saveDate();
    setIsOpenModal(false);
  };

  const gotoLogin = () => {
    handleClose();
    callAfterLogin(async () => {
      // const [err, result] = await to(giftQuery());
    });
  };

  const track = () => {
    const coupons: any[] = [];
    const list = [...(couponList || []), ...(shippingCoupons || [])];
    if (list && list.length > 0) {
      list.forEach((item: any) => {
        coupons.push({
          couponId: item?.couponId,
          batchId: item?.batchId || "-100",
          couponTag: "0", // 0 新人礼包
          use_threshold: item?.quota,
          couponType: item?.couponType === 1 ? "1" : "2",
          couponSave: item?.discount,
        });
      });
    }
    return {
      coupon: coupons,
      is_login: isLogin ? ("1" as const) : ("0" as const),
    };
  };

  // ---------------- 风格化文字 ---------------- //
  const formatRed = (redText: any) => {
    return `<span style="font-weight: 900; color: #CC0C1C; font-family: 'var(--JDZhengHT-EN)';">${redText}</span>`;
  };
  const formatBold = (boldText: any) => {
    return `<span style="font-weight: 900; color: #1A1A1A; font-family: 'var(--JDZhengHT-EN)';">${boldText}</span>`;
  };

  // ---------------- 单独处理优惠券/运费券多语言文案 ---------------- //
  const couponInfo = (list: any) => {
    let text = "";
    if (list && list.length > 0) {
      text = t("gift.couponDetail", { num: list?.length });
      list.forEach((item: any, index: number) => {
        if (index > 0) {
          text += t("gift.connector"); // 在不是第一个元素时添加 +
        }
        text += t("gift.offText", { discount: currencySymbol + item?.discount, quota: currencySymbol + item?.quota });
      });
    }
    return text;
  };

  const deliveryInfo = (list: any) => {
    if (list && list.length > 0) {
      return t("gift.deliveryInfo", { num: list.length });
    }
  };

  const couponPromptText = () => {
    let text = "";
    if (shippingCoupons && shippingCoupons.length > 0 && couponList && couponList.length > 0) {
      text = t("gift.totalDiscount")
        .replace("##discount##", `${formatRed(`${currencySymbol}${totalDiscount}`)}`)
        .replace("##num##", `${formatRed(shippingCoupons.length)}`);
    } else if (shippingCoupons && shippingCoupons.length > 0) {
      text = `${listShippingText()}`;
    } else if (couponList && couponList.length > 0) {
      text = `${listCouponText()}`;
    }
    const freeDeliveryText = t("gift.freeBold"); // 获取动态文本
    // 这里自定义（##discount##、free-delivery）根据多语言匹配规则
    return text.replace(/free-delivery/g, formatBold(freeDeliveryText));
  };

  const listCouponText = () => {
    if (couponList && couponList.length > 0) {
      return t("gift.freeNum")
        .replace("##discount##", `${formatRed(`${currencySymbol}${totalDiscount}`)}`)
        .replace("##num##", `${formatRed(couponList.length)}`);
    }
  };
  const listShippingText = () => {
    if (shippingCoupons && shippingCoupons.length > 0) {
      let text = t("gift.shippingNum").replace("##num##", `${formatRed(shippingCoupons.length)}`);
      const freeDeliveryText = t("gift.freeBold");
      return text.replace(/free-delivery/g, formatBold(freeDeliveryText));
    }
  };

  return (
    <>
      {isOpenModal && (
        <div className={styles.container}>
          <div
            className={styles.box}
            exptag={"exp|" + EXPOSURE_RECORD.NewCustomerGift_GiftLayerExpo}
            data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
            data-exptag-json-param={JSON.stringify(track())}
          >
            <div className={styles.topBg}>
              <GiftCloseIcon
                className={styles.close}
                onClick={() => {
                  handleClose();
                  sendClickTrack.NewCustomerGift_GiftLayer({ postcode: "-100", clickPos: "0", ...track() });
                }}
              />
              <div className={styles.title}>{activityTitle}</div>
              <div className={styles.animBox}>
                <LottieAnimation animationData={giftLottie} width="180px" height="151px" loop={false} />
                {/* <div className={styles.serviceInfo}>*Terms and Services Apply</div> */}
              </div>
            </div>
            <div className={styles.text}>
              <div className={styles.overview} dangerouslySetInnerHTML={{ __html: couponPromptText() }} />
              <div className={styles.detail}>
                <p>{couponList && couponList.length > 0 && couponInfo(couponList)}</p>
                <p>{shippingCoupons && shippingCoupons.length > 0 && deliveryInfo(shippingCoupons)}</p>
              </div>
            </div>
            <Button className={styles.buttonCheck} onClick={() => {
              gotoLogin();
              sendClickTrack.NewCustomerGift_GiftLayer({ postcode: "-100", clickPos: "2", ...track() })
            }}>
              {isLogin ? `${t("gift.got")}` : `${t("gift.claim")}`}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default GiftModal;
